"use client";

// packages/observability/src/web-context.ts
// Browser-only client log context: a tiny mutable singleton + an OTel
// LogRecordProcessor that stamps every emitted record with workspace_id,
// user_id, and route. Lets SigNoz answer "who/where" for any client-side
// error without paying a server roundtrip per log.
//
// The store is intentionally global, not React state — `logger.error` can
// fire from anywhere (event handlers, timers, instrumentation), and we
// don't want every callsite to pass context through. Updates flow in from
// `LogContextTracker` (route + workspace + user) and from the early-error
// shim (no context, defaults).
//
// This client module supports SSR without browser-only side effects. Server
// Components must import startup literals from ./early-error-shim-source:
// exports from this boundary become client references in the RSC payload.

import type { Context } from "@opentelemetry/api";
import type { LogRecordProcessor } from "@opentelemetry/sdk-logs";
import type { SdkLogRecord } from "@opentelemetry/sdk-logs";
import type {
  ReadableSpan,
  Span,
  SpanProcessor,
} from "@opentelemetry/sdk-trace-base";
import type { Logger } from "./logger";
import { EARLY_ERROR_BUFFER_KEY } from "./early-error-shim-source";

export {
  EARLY_ERROR_BUFFER_KEY,
  EARLY_ERROR_SHIM_SOURCE,
} from "./early-error-shim-source";

export interface ClientLogContext {
  user_id?: string;
  workspace_id?: string;
  user_email?: string;
  route?: string;
  /** Free-form additional attributes, merged onto every emit. */
  extra?: Record<string, string | number | boolean>;
}

const ATTRIBUTE_KEYS = [
  "user_id",
  "workspace_id",
  "user_email",
  "route",
] as const;

let store: ClientLogContext = {};

/**
 * Replace or merge fields into the client log context. Subsequent
 * `logger.*` emits will carry these attributes. Pass `undefined` for any
 * field to clear it (e.g. on sign-out).
 */
export function setClientLogContext(next: Partial<ClientLogContext>): void {
  store = { ...store, ...next };
}

export function getClientLogContext(): Readonly<ClientLogContext> {
  return store;
}

/**
 * Reads `window.posthog.get_session_id()` lazily — at the moment the record
 * is being processed, not at instrumentation-client.ts boot. The PostHog
 * session id is assigned by the SDK on first event capture, which can be
 * after `posthog.init` returns, so a synchronous `setClientLogContext`
 * call from instrumentation-client misses every record between init and
 * the first pageview. Reading at emit-time always gets the freshest id.
 */
function currentPostHogSessionId(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const ph = (
    window as unknown as {
      posthog?: { get_session_id?: () => string | null | undefined };
    }
  ).posthog;
  const id = ph?.get_session_id?.();
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

/**
 * OTel LogRecordProcessor that copies the current client context onto each
 * record's attributes before downstream processors (batch + export) see it.
 * Order matters when registering: this must run BEFORE `BatchLogRecordProcessor`
 * so the exporter sees the enriched record.
 */
export class ContextEnrichingLogProcessor implements LogRecordProcessor {
  onEmit(record: SdkLogRecord, _context?: Context): void {
    const ctx = store;
    for (const key of ATTRIBUTE_KEYS) {
      const value = ctx[key];
      if (typeof value === "string" && value.length > 0) {
        record.setAttribute(key, value);
      }
    }
    if (ctx.extra) {
      for (const [k, v] of Object.entries(ctx.extra)) {
        record.setAttribute(k, v);
      }
    }
    // PostHog session id — read live at emit time so the very first record
    // after PostHog init carries it. The store-based path (set via
    // `setClientLogContext` from `posthog.onSessionId`) backstops when the
    // SDK isn't available (e.g. early-error buffer drain before init).
    if (!ctx.extra?.posthog_session_id) {
      const sid = currentPostHogSessionId();
      if (sid) {
        record.setAttribute("posthog_session_id", sid);
      }
    }
  }
  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

/**
 * Mirrors `ContextEnrichingLogProcessor` for spans. Reads workspace_id /
 * user_id / posthog_session_id at span-start time and stamps them as
 * attributes. Without this, SigNoz trace search by `posthog_session_id`
 * returns nothing — only logs carry the join key, and you can't pivot from
 * a slow trace back to a replay.
 */
export class ContextEnrichingSpanProcessor implements SpanProcessor {
  onStart(span: Span, _context: Context): void {
    const ctx = store;
    for (const key of ATTRIBUTE_KEYS) {
      const value = ctx[key];
      if (typeof value === "string" && value.length > 0) {
        span.setAttribute(key, value);
      }
    }
    if (ctx.extra) {
      for (const [k, v] of Object.entries(ctx.extra)) {
        span.setAttribute(k, v);
      }
    }
    const sid = currentPostHogSessionId();
    if (sid) {
      span.setAttribute("posthog_session_id", sid);
    }
  }
  onEnd(_span: ReadableSpan): void {
    // No-op; the BatchSpanProcessor downstream handles export.
  }
  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

// ----------------------------------------------------------------------------
// Early-error buffer
//
// The OTel SDK only initialises post-hydration (lazy by design — booting a
// LoggerProvider before LCP costs ~30 ms). Errors thrown between the first
// HTML byte and that init point would otherwise be invisible. The root
// layout renders an inline `<script>` (see `early-error-shim.tsx`) that
// installs cheap `error` + `unhandledrejection` listeners and pushes them
// into `window.__atriamEarlyErrors`. Once the SDK is up,
// `drainEarlyErrorBuffer()` replays each entry through PostHog (preferred)
// or the real logger (fallback when PostHog has not initialised).
// ----------------------------------------------------------------------------

export interface EarlyErrorEntry {
  /** ms since epoch when the error was captured */
  t: number;
  /** "error" for window.onerror, "rejection" for unhandledrejection */
  kind: "error" | "rejection";
  message: string;
  source?: string;
  line?: number;
  col?: number;
  stack?: string;
}

interface WindowWithEarlyErrors {
  [EARLY_ERROR_BUFFER_KEY]?: EarlyErrorEntry[];
}

/**
 * Drain the pre-hydration error buffer. Prefer PostHog (the canonical
 * exception store) when it is initialised; fall back to `logger.error` so
 * the breadcrumb still reaches SigNoz when PostHog is disabled or has not
 * booted yet. Idempotent — clears the buffer so a second drain is a no-op.
 */
export function drainEarlyErrorBuffer(logger: Logger): void {
  if (typeof window === "undefined") {
    return;
  }
  const w = window as unknown as WindowWithEarlyErrors;
  const buf = w[EARLY_ERROR_BUFFER_KEY];
  if (!buf || buf.length === 0) {
    return;
  }

  // PostHog assigns itself to `window.posthog` on init. Read via global to
  // keep this module server-bundle-safe (importing posthog-js statically
  // would pull it into every SSR bundle).
  const ph = (
    window as unknown as {
      posthog?: {
        captureException?: (e: Error, p?: Record<string, unknown>) => void;
      };
    }
  ).posthog;
  const phReady = typeof ph?.captureException === "function";

  for (const entry of buf) {
    const err = new Error(entry.message);
    if (entry.stack) {
      err.stack = entry.stack;
    }
    const props = {
      "error.kind": entry.kind,
      "error.source": entry.source,
      "error.line": entry.line,
      "error.col": entry.col,
      "error.captured_pre_hydration_ms": entry.t,
      "error.replay_lag_ms": Date.now() - entry.t,
    };
    if (phReady) {
      ph.captureException!(err, props);
    } else {
      logger.error(entry.message, { ...props, "error.stack": entry.stack });
    }
  }
  buf.length = 0;
}
